import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Award,
  Star,
  Save,
  Upload,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BAR_VIBE_OPTIONS = [
  'Sports Bar',
  'Dive Bar',
  'Craft Cocktail',
  'Nightclub',
  'Restaurant Bar',
  'Live Music',
  'Patio Bar',
  'Event Venue',
  'Brewery',
  'Upscale Lounge',
];

const SHIFT_SERVICE_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'cocktail_focused', label: 'Cocktail-focused' },
  { value: 'volume_focused', label: 'Volume-focused' },
  { value: 'balanced', label: 'Balanced' },
];

function ProfilePage() {
  const { user, isBarOwner, isBartender } = useAuth();
  const [profile, setProfile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [editing, setEditing] = useState(true); // Always in editing mode by default
  
  // Bartender profile form
  const [bartenderForm, setBartenderForm] = useState({
    age: '',
    experience_years: '',
    skills: [],
    bio: '',
    hourly_rate_min: '',
    hourly_rate_max: '',
    location: {
      city: '',
      state: ''
    },
    certifications: [],
    availability: {},
    available_tonight: false
  });

  // Bar owner profile form
  const [barForm, setBarForm] = useState({
    business_name: '',
    establishment_type: '',
    location: {
      address: '',
      city: '',
      state: ''
    },
    description: '',
    about_bar: '',
    atmosphere: '',
    clientele: '',
    culture: '',
    service_style: '',
    bartender_expectations: '',
    vibe_tags: [],
    custom_vibe_tags: [],
    gallery_images: [],
    shift_environment: {
      busiest_nights: [],
      dress_code: '',
      service_mode: '',
      food_service: null,
      live_entertainment: null,
    },
    website: '',
    license_number: ''
  });

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      if (isBartender) {
        const response = await axios.get(`/bartenders/${user.id}`);
        console.log('Profile response:', response.data); // Debug log
        
        // Handle case where profile might not exist yet
        const profileData = response.data.profile || {};
        setProfile(profileData);
        
        setBartenderForm({
          age: profileData.age || '',
          experience_years: profileData.experience_years || 0,
          skills: profileData.skills || [],
          bio: profileData.bio || '',
          hourly_rate_min: profileData.hourly_rate_min || '',
          hourly_rate_max: profileData.hourly_rate_max || '',
          location: profileData.location || { city: '', state: '' },
          certifications: profileData.certifications || [],
          availability: profileData.availability || {},
          available_tonight: profileData.available_tonight || false
        });
      } else if (isBarOwner) {
        const response = await axios.get('/bar/profile/me');
        const profileData = response.data || {};
        setProfile(profileData);
        setBarForm({
          business_name: profileData.business_name || '',
          establishment_type: profileData.establishment_type || 'bar',
          location: profileData.location || { address: '', city: '', state: '' },
          description: profileData.description || '',
          about_bar: profileData.about_bar || profileData.description || '',
          atmosphere: profileData.atmosphere || '',
          clientele: profileData.clientele || '',
          culture: profileData.culture || '',
          service_style: profileData.service_style || '',
          bartender_expectations: profileData.bartender_expectations || '',
          vibe_tags: profileData.vibe_tags || [],
          custom_vibe_tags: profileData.custom_vibe_tags || [],
          gallery_images: profileData.gallery_images || [],
          shift_environment: {
            busiest_nights: profileData.shift_environment?.busiest_nights || [],
            dress_code: profileData.shift_environment?.dress_code || '',
            service_mode: profileData.shift_environment?.service_mode || '',
            food_service: typeof profileData.shift_environment?.food_service === 'boolean' ? profileData.shift_environment.food_service : null,
            live_entertainment: typeof profileData.shift_environment?.live_entertainment === 'boolean' ? profileData.shift_environment.live_entertainment : null,
          },
          website: profileData.website || '',
          license_number: profileData.license_number || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // If profile doesn't exist, create empty one
      if (error.response?.status === 404 && isBartender) {
        const emptyProfile = {
          age: null,
          experience_years: 0,
          skills: [],
          bio: '',
          hourly_rate_min: null,
          hourly_rate_max: null,
          location: { city: '', state: '' },
          certifications: [],
          availability: {},
          available_tonight: false,
          average_rating: 0,
          total_reviews: 0
        };
        setProfile(emptyProfile);
        setBartenderForm({
          age: '',
          experience_years: 0,
          skills: [],
          bio: '',
          hourly_rate_min: '',
          hourly_rate_max: '',
          location: { city: '', state: '' },
          certifications: [],
          availability: {},
          available_tonight: false
        });
      } else if (error.response?.status === 404 && isBarOwner) {
        const emptyProfile = {
          business_name: '',
          establishment_type: 'bar',
          location: { address: '', city: '', state: '' },
          description: '',
          about_bar: '',
          atmosphere: '',
          clientele: '',
          culture: '',
          service_style: '',
          bartender_expectations: '',
          vibe_tags: [],
          custom_vibe_tags: [],
          gallery_images: [],
          shift_environment: {
            busiest_nights: [],
            dress_code: '',
            service_mode: '',
            food_service: null,
            live_entertainment: null,
          },
          website: '',
          license_number: '',
        };
        setProfile(emptyProfile);
        setBarForm(emptyProfile);
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [isBartender, isBarOwner, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchProfile();
  }, [user?.id, fetchProfile]);

  useEffect(() => {
    setAvatarPreview(user?.profile_picture || '');
  }, [user?.profile_picture]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      if (isBartender) {
        await axios.put('/bartenders/profile', {
          ...bartenderForm,
          age: parseInt(bartenderForm.age) || 0,
          experience_years: parseInt(bartenderForm.experience_years) || 0,
          hourly_rate_min: parseFloat(bartenderForm.hourly_rate_min) || null,
          hourly_rate_max: parseFloat(bartenderForm.hourly_rate_max) || null
        });
      } else {
        await axios.put('/bar/profile/me', {
          ...barForm,
          shift_environment: {
            ...barForm.shift_environment,
            busiest_nights: barForm.shift_environment?.busiest_nights || [],
          },
        });
      }
      
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingPhoto(true);
    try {
      const imageDataUrl = await readFileAsDataUrl(file);
      const response = await axios.put('/users/profile-photo', {
        profile_picture: imageDataUrl,
      });

      const updatedPicture = response.data?.profile_picture || imageDataUrl;
      setAvatarPreview(updatedPicture);

      try {
        const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({
          ...existingUser,
          profile_picture: updatedPicture,
        }));
      } catch {
        // no-op local storage fallback
      }

      toast.success('Profile photo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const remainingSlots = 10 - (barForm.gallery_images?.length || 0);
    if (remainingSlots <= 0) {
      toast.error('Gallery limit reached (max 10 photos)');
      return;
    }

    setUploadingPhoto(true);
    try {
      const selected = files.slice(0, remainingSlots);
      const converted = [];

      for (const file of selected) {
        if (!file.type.startsWith('image/')) {
          continue;
        }
        if (file.size > 4 * 1024 * 1024) {
          toast.warning(`${file.name} skipped (max 4MB each)`);
          continue;
        }
        const dataUrl = await readFileAsDataUrl(file);
        converted.push(dataUrl);
      }

      setBarForm((prev) => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), ...converted].slice(0, 10),
      }));

      if (converted.length > 0) {
        toast.success(`${converted.length} photo${converted.length > 1 ? 's' : ''} added`);
      }
    } catch (error) {
      console.error('Failed to process gallery photos:', error);
      toast.error('Failed to upload gallery photos');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const removeGalleryImage = (index) => {
    setBarForm((prev) => ({
      ...prev,
      gallery_images: (prev.gallery_images || []).filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const toggleVibeTag = (tag) => {
    setBarForm((prev) => {
      const existingTags = prev.vibe_tags || [];
      const hasTag = existingTags.includes(tag);
      return {
        ...prev,
        vibe_tags: hasTag ? existingTags.filter((item) => item !== tag) : [...existingTags, tag],
      };
    });
  };

  const handleCustomVibeTagsChange = (value) => {
    const tags = value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item)
      .slice(0, 10);

    setBarForm((prev) => ({
      ...prev,
      custom_vibe_tags: tags,
    }));
  };

  const handleSkillsChange = (skillsString) => {
    const skills = skillsString.split(',').map(skill => skill.trim()).filter(skill => skill);
    setBartenderForm({ ...bartenderForm, skills });
  };

  const handleCertificationsChange = (certsString) => {
    const certifications = certsString.split(',').map(cert => cert.trim()).filter(cert => cert);
    setBartenderForm({ ...bartenderForm, certifications });
  };

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);
    
    try {
      // Get GPS coordinates
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;

      // Use reverse geocoding to get city and state
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`
      );
      const data = await response.json();

      const city = data.address?.city || data.address?.town || data.address?.village || '';
      const state = data.address?.state || '';

      if (city || state) {
        setBartenderForm({
          ...bartenderForm,
          location: { city, state }
        });
        toast.success(`Location detected: ${city}${state ? ', ' + state : ''}`);
      } else {
        toast.warning('Could not determine city/state from GPS. Please enter manually.');
      }
    } catch (error) {
      console.error('Location detection failed:', error);
      if (error.code === 1) {
        toast.error('Location access denied. Please enable location permissions and try again.');
      } else if (error.code === 2) {
        toast.error('Location unavailable. Please check your device settings.');
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to detect location. Please enter manually.');
      }
    } finally {
      setDetectingLocation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed']">
            My Profile
          </h1>
          <p className="text-lg text-muted-foreground">
            {isBartender ? 'Manage your bartender profile and showcase your skills' : 'Manage your bar information and business details'}
          </p>
        </div>
      </div>

      {/* Profile Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Basic Info */}
        <div className="space-y-6">
          {/* Avatar Card */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/5">
            <CardContent className="p-6 text-center">
              <Avatar className="h-32 w-32 mx-auto mb-4">
                <AvatarImage 
                  src={avatarPreview || user?.profile_picture} 
                  alt={`${user?.first_name} ${user?.last_name}`}
                />
                <AvatarFallback className="bg-primary/10 text-primary text-3xl">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-white mb-2">
                {user?.first_name} {user?.last_name}
              </h2>
              <Badge className="mb-4">
                {isBarOwner ? 'Bar Owner' : 'Bartender'}
              </Badge>
              {profile?.verification_status === 'approved' && (
                <Badge className="mb-4 ml-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid="profile-verified-badge">
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              )}
              {isBartender && (bartenderForm.available_tonight || profile?.available_tonight) && (
                <Badge className="mb-4 ml-2 bg-green-500/20 text-green-400 border-green-500/30" data-testid="profile-available-tonight-badge">
                  Available Tonight
                </Badge>
              )}
              <input
                type="file"
                id="avatar-photo-upload"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="profile-photo-upload-input"
              />
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                data-testid="upload-photo-btn"
                onClick={() => document.getElementById('avatar-photo-upload').click()}
                disabled={uploadingPhoto}
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploadingPhoto ? 'Uploading...' : 'Upload Profile Photo'}
              </Button>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/5">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] uppercase">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center text-sm">
                <Mail className="h-4 w-4 mr-3 text-muted-foreground" />
                <span className="text-muted-foreground">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center text-sm">
                  <Phone className="h-4 w-4 mr-3 text-muted-foreground" />
                  <span className="text-muted-foreground">{user.phone}</span>
                </div>
              )}
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 mr-3 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Joined {new Date(user?.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Bartender Stats - Only show if user has reviews */}
          {isBartender && profile && (profile.total_reviews > 0) && (
            <Card className="bg-white/5 backdrop-blur-md border border-white/5">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase">Performance Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-[#F5C15D]" />
                    <span className="text-sm text-muted-foreground">Average Rating</span>
                  </div>
                  <span className="font-semibold text-[#F5C15D]">
                    {profile.average_rating ? profile.average_rating.toFixed(1) : 'N/A'} ⭐
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-sm text-muted-foreground">Total Reviews</span>
                  </div>
                  <span className="font-semibold">{profile.total_reviews || 0}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Getting Started Card for new users */}
          {isBartender && profile && (profile.total_reviews === 0) && (
            <Card className="bg-gradient-to-br from-primary/10 to-[#F5C15D]/10 border border-primary/20">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase text-primary">Get Started</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Complete your profile to attract more job opportunities!
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-primary" />
                    <span className={profile.bio ? 'text-green-400' : 'text-muted-foreground'}>
                      {profile.bio ? '✓ Bio completed' : 'Add your bio'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-primary" />
                    <span className={profile.skills?.length > 0 ? 'text-green-400' : 'text-muted-foreground'}>
                      {profile.skills?.length > 0 ? '✓ Skills added' : 'Add your skills'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Award className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-muted-foreground">
                      Ratings will appear after your first job
                    </span>
                  </div>
                </div>
                
                {/* Save Profile Button */}
                <div className="flex justify-end pt-6 border-t border-white/10">
                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={saving}
                    className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] px-8 py-3 text-lg"
                    data-testid="save-profile-btn"
                  >
                    <Save className="mr-2 h-5 w-5" />
                    {saving ? 'Saving Profile...' : 'Save Profile'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {isBartender ? (
            /* Bartender Profile Form */
            <Card className="bg-white/5 backdrop-blur-md border border-white/5">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase">Professional Details</CardTitle>
                <CardDescription>
                  Fill in your professional information to attract more job opportunities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      type="number"
                      min="18"
                      max="80"
                      value={bartenderForm.age}
                      onChange={(e) => setBartenderForm({...bartenderForm, age: e.target.value})}
                      placeholder="Enter your age"
                      data-testid="age-input"
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience</Label>
                    <Input
                      id="experience"
                      type="number"
                      min="0"
                      max="50"
                      value={bartenderForm.experience_years}
                      onChange={(e) => setBartenderForm({...bartenderForm, experience_years: e.target.value})}
                      placeholder="Years of experience"
                      data-testid="experience-input"
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Profile Completion</Label>
                    <div className="text-sm text-muted-foreground">
                      {(() => {
                        const completed = [
                          profile?.age,
                          profile?.bio,
                          profile?.skills?.length > 0,
                          profile?.location?.city
                        ].filter(Boolean).length;
                        const total = 4;
                        return `${completed}/${total} completed`;
                      })()}
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  {editing ? (
                    <Textarea
                      id="bio"
                      placeholder="Tell us about yourself, your experience, and what makes you a great bartender..."
                      rows={4}
                      value={bartenderForm.bio}
                      onChange={(e) => setBartenderForm({...bartenderForm, bio: e.target.value})}
                      data-testid="bio-input"
                      className="resize-none"
                    />
                  ) : (
                    <div 
                      className="text-sm text-muted-foreground leading-relaxed cursor-pointer hover:text-primary transition-colors min-h-[100px] p-3 border border-white/10 rounded-md hover:border-primary/30"
                      onClick={() => setEditing(true)}
                      title="Click to edit your bio"
                    >
                      {profile?.bio || (
                        <span className="italic text-muted-foreground/70">
                          Click here to add your bio - tell bar owners about your experience, specialties, and what makes you unique!
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skills">Skills & Specialties</Label>
                  {editing ? (
                    <Input
                      id="skills"
                      placeholder="Mixology, Cocktails, Wine Service, Craft Beer, etc. (comma-separated)"
                      value={bartenderForm.skills.join(', ')}
                      onChange={(e) => handleSkillsChange(e.target.value)}
                      data-testid="skills-input"
                      className="h-10"
                    />
                  ) : (
                    <div 
                      className="cursor-pointer hover:border-primary/30 transition-colors p-3 border border-white/10 rounded-md min-h-[40px] flex items-center"
                      onClick={() => setEditing(true)}
                      title="Click to edit your skills"
                    >
                      <div className="flex flex-wrap gap-2">
                        {profile?.skills?.length > 0 ? (
                          profile.skills.map((skill, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground/70 italic">
                            Click here to add your bartending skills and specialties
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="certifications">Certifications</Label>
                  {editing ? (
                    <Input
                      id="certifications"
                      placeholder="TIPS, ServSafe, etc. (comma-separated)"
                      value={bartenderForm.certifications.join(', ')}
                      onChange={(e) => handleCertificationsChange(e.target.value)}
                      data-testid="certifications-input"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profile?.certifications?.length > 0 ? (
                        profile.certifications.map((cert, index) => (
                          <Badge key={index} variant="outline">{cert}</Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No certifications listed</span>
                      )}
                    </div>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rate_min">Minimum Hourly Rate ($)</Label>
                    {editing ? (
                      <Input
                        id="rate_min"
                        type="number"
                        min="10"
                        max="100"
                        step="0.50"
                        placeholder="20.00"
                        value={bartenderForm.hourly_rate_min}
                        onChange={(e) => setBartenderForm({...bartenderForm, hourly_rate_min: e.target.value})}
                        data-testid="rate-min-input"
                      />
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {profile?.hourly_rate_min ? `$${profile.hourly_rate_min}/hr` : 'Not specified'}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rate_max">Maximum Hourly Rate ($)</Label>
                    {editing ? (
                      <Input
                        id="rate_max"
                        type="number"
                        min="10"
                        max="100"
                        step="0.50"
                        placeholder="35.00"
                        value={bartenderForm.hourly_rate_max}
                        onChange={(e) => setBartenderForm({...bartenderForm, hourly_rate_max: e.target.value})}
                        data-testid="rate-max-input"
                      />
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {profile?.hourly_rate_max ? `$${profile.hourly_rate_max}/hr` : 'Not specified'}
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Location</Label>
                    {editing && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={detectLocation}
                        disabled={detectingLocation}
                      >
                        <MapPin className="mr-2 h-4 w-4" />
                        {detectingLocation ? 'Detecting...' : 'Use My Location'}
                      </Button>
                    )}
                  </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    {editing ? (
                      <Input
                        id="city"
                        placeholder="San Francisco"
                        value={bartenderForm.location.city}
                        onChange={(e) => setBartenderForm({
                          ...bartenderForm, 
                          location: {...bartenderForm.location, city: e.target.value}
                        })}
                        data-testid="city-input"
                        className="h-10"
                      />
                    ) : (
                      <div 
                        className="text-sm text-muted-foreground flex items-center cursor-pointer hover:text-primary transition-colors p-2 border border-white/10 rounded-md hover:border-primary/30 h-10"
                        onClick={() => setEditing(true)}
                        title="Click to edit location"
                      >
                        <MapPin className="h-4 w-4 mr-1" />
                        {profile?.location?.city ? 
                          `${profile.location.city}${profile.location.state ? `, ${profile.location.state}` : ''}` : 
                          'Click to add your location'
                        }
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    {editing ? (
                      <Input
                        id="state"
                        placeholder="CA"
                        value={bartenderForm.location.state}
                        onChange={(e) => setBartenderForm({
                          ...bartenderForm, 
                          location: {...bartenderForm.location, state: e.target.value}
                        })}
                        data-testid="state-input"
                        className="h-10"
                      />
                    ) : null}
                  </div>
                </div>
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-white/10">
                  <Button 
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="min-w-[120px]"
                    data-testid="save-profile-btn"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/5 backdrop-blur-md border border-white/5">
              <CardHeader>
                <CardTitle className="font-['Barlow_Condensed'] uppercase" data-testid="bar-profile-editor-title">Bar & Owner Showcase</CardTitle>
                <CardDescription data-testid="bar-profile-editor-description">
                  Build a visual profile so bartenders can understand your vibe before applying.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name" data-testid="business-name-label">Business Name</Label>
                    <Input
                      id="business_name"
                      value={barForm.business_name}
                      onChange={(e) => setBarForm({ ...barForm, business_name: e.target.value })}
                      placeholder="Night Shift Lounge"
                      data-testid="business-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="establishment_type" data-testid="establishment-type-label">Establishment Type</Label>
                    <Input
                      id="establishment_type"
                      value={barForm.establishment_type}
                      onChange={(e) => setBarForm({ ...barForm, establishment_type: e.target.value })}
                      placeholder="Cocktail Bar"
                      data-testid="establishment-type-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bar_address" data-testid="bar-address-label">Address</Label>
                    <Input
                      id="bar_address"
                      value={barForm.location.address}
                      onChange={(e) => setBarForm({ ...barForm, location: { ...barForm.location, address: e.target.value } })}
                      placeholder="123 Main St"
                      data-testid="bar-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bar_city" data-testid="bar-city-label">City</Label>
                    <Input
                      id="bar_city"
                      value={barForm.location.city}
                      onChange={(e) => setBarForm({ ...barForm, location: { ...barForm.location, city: e.target.value } })}
                      placeholder="Youngstown"
                      data-testid="bar-city-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="about_bar" data-testid="about-bar-label">About Our Bar</Label>
                  <Textarea
                    id="about_bar"
                    value={barForm.about_bar}
                    onChange={(e) => setBarForm({ ...barForm, about_bar: e.target.value, description: e.target.value })}
                    rows={5}
                    placeholder="Describe the atmosphere, clientele, and what makes your bar unique..."
                    data-testid="about-bar-textarea"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="atmosphere" data-testid="atmosphere-label">Atmosphere</Label>
                    <Textarea
                      id="atmosphere"
                      rows={3}
                      value={barForm.atmosphere}
                      onChange={(e) => setBarForm({ ...barForm, atmosphere: e.target.value })}
                      placeholder="Energy level, music, crowd vibe"
                      data-testid="atmosphere-textarea"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientele" data-testid="clientele-label">Clientele</Label>
                    <Textarea
                      id="clientele"
                      rows={3}
                      value={barForm.clientele}
                      onChange={(e) => setBarForm({ ...barForm, clientele: e.target.value })}
                      placeholder="Who your regular guests are"
                      data-testid="clientele-textarea"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="culture" data-testid="culture-label">Team Culture</Label>
                    <Textarea
                      id="culture"
                      rows={3}
                      value={barForm.culture}
                      onChange={(e) => setBarForm({ ...barForm, culture: e.target.value })}
                      placeholder="How your team works together"
                      data-testid="culture-textarea"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bartender_expectations" data-testid="bartender-expectations-label">What You Look For in Bartenders</Label>
                    <Textarea
                      id="bartender_expectations"
                      rows={3}
                      value={barForm.bartender_expectations}
                      onChange={(e) => setBarForm({ ...barForm, bartender_expectations: e.target.value })}
                      placeholder="Traits/skills you value most"
                      data-testid="bartender-expectations-textarea"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label data-testid="vibe-tags-label">Bar Vibe Tags</Label>
                  <div className="flex flex-wrap gap-2" data-testid="vibe-tags-options">
                    {BAR_VIBE_OPTIONS.map((tag) => {
                      const selected = (barForm.vibe_tags || []).includes(tag);
                      return (
                        <Button
                          key={tag}
                          type="button"
                          size="sm"
                          variant={selected ? 'default' : 'outline'}
                          onClick={() => toggleVibeTag(tag)}
                          data-testid={`vibe-tag-option-${tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          {tag}
                        </Button>
                      );
                    })}
                  </div>
                  <Input
                    value={(barForm.custom_vibe_tags || []).join(', ')}
                    onChange={(e) => handleCustomVibeTagsChange(e.target.value)}
                    placeholder="Custom vibe tags (comma separated)"
                    data-testid="custom-vibe-tags-input"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label data-testid="bar-gallery-label">Photo Gallery ({(barForm.gallery_images || []).length}/10)</Label>
                    <>
                      <input
                        type="file"
                        id="bar-gallery-upload"
                        accept="image/*"
                        multiple
                        onChange={handleGalleryUpload}
                        className="hidden"
                        data-testid="bar-gallery-upload-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('bar-gallery-upload').click()}
                        disabled={uploadingPhoto || (barForm.gallery_images || []).length >= 10}
                        data-testid="bar-gallery-upload-button"
                      >
                        <Upload className="h-4 w-4 mr-1" />Add Photos
                      </Button>
                    </>
                  </div>
                  {(barForm.gallery_images || []).length === 0 ? (
                    <div className="rounded-md border border-dashed border-white/10 p-4 text-sm text-muted-foreground" data-testid="bar-gallery-empty-state">
                      Upload photos to showcase your bar interior, patio, events, team, and atmosphere.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="bar-gallery-grid">
                      {(barForm.gallery_images || []).map((image, index) => (
                        <div key={`${image.slice(0, 20)}-${index}`} className="relative rounded-md overflow-hidden border border-white/10" data-testid={`bar-gallery-item-${index}`}>
                          <img src={image} alt={`Bar gallery ${index + 1}`} className="w-full h-28 object-cover" data-testid={`bar-gallery-image-${index}`} />
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="absolute top-2 right-2 h-7 px-2"
                            onClick={() => removeGalleryImage(index)}
                            data-testid={`bar-gallery-remove-${index}`}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-lg border border-white/10 p-4" data-testid="shift-environment-section">
                  <p className="text-sm font-medium text-white" data-testid="shift-environment-title">Shift Environment Details (Optional)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="busiest_nights" data-testid="busiest-nights-label">Busiest Nights</Label>
                      <Input
                        id="busiest_nights"
                        value={(barForm.shift_environment.busiest_nights || []).join(', ')}
                        onChange={(e) => setBarForm({
                          ...barForm,
                          shift_environment: {
                            ...barForm.shift_environment,
                            busiest_nights: e.target.value.split(',').map((item) => item.trim()).filter((item) => item).slice(0, 7),
                          },
                        })}
                        placeholder="Thursday, Friday, Saturday"
                        data-testid="busiest-nights-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dress_code" data-testid="dress-code-label">Dress Code</Label>
                      <Input
                        id="dress_code"
                        value={barForm.shift_environment.dress_code || ''}
                        onChange={(e) => setBarForm({
                          ...barForm,
                          shift_environment: { ...barForm.shift_environment, dress_code: e.target.value },
                        })}
                        placeholder="All black, casual, upscale"
                        data-testid="dress-code-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="service_mode" data-testid="service-mode-label">Service Style</Label>
                      <select
                        id="service_mode"
                        value={barForm.shift_environment.service_mode || ''}
                        onChange={(e) => setBarForm({
                          ...barForm,
                          shift_environment: { ...barForm.shift_environment, service_mode: e.target.value },
                        })}
                        className="w-full h-10 rounded-md border border-white/10 bg-transparent px-3 text-sm"
                        data-testid="service-mode-select"
                      >
                        {SHIFT_SERVICE_OPTIONS.map((option) => (
                          <option key={option.value || 'empty'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="food_service" data-testid="food-service-label">Food Service</Label>
                      <select
                        id="food_service"
                        value={barForm.shift_environment.food_service === null ? '' : String(barForm.shift_environment.food_service)}
                        onChange={(e) => setBarForm({
                          ...barForm,
                          shift_environment: {
                            ...barForm.shift_environment,
                            food_service: e.target.value === '' ? null : e.target.value === 'true',
                          },
                        })}
                        className="w-full h-10 rounded-md border border-white/10 bg-transparent px-3 text-sm"
                        data-testid="food-service-select"
                      >
                        <option value="">Not specified</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="live_entertainment" data-testid="live-entertainment-label">Live Entertainment</Label>
                      <select
                        id="live_entertainment"
                        value={barForm.shift_environment.live_entertainment === null ? '' : String(barForm.shift_environment.live_entertainment)}
                        onChange={(e) => setBarForm({
                          ...barForm,
                          shift_environment: {
                            ...barForm.shift_environment,
                            live_entertainment: e.target.value === '' ? null : e.target.value === 'true',
                          },
                        })}
                        className="w-full h-10 rounded-md border border-white/10 bg-transparent px-3 text-sm"
                        data-testid="live-entertainment-select"
                      >
                        <option value="">Not specified</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="website" data-testid="website-label">Website</Label>
                    <Input
                      id="website"
                      value={barForm.website}
                      onChange={(e) => setBarForm({ ...barForm, website: e.target.value })}
                      placeholder="https://yourbar.com"
                      data-testid="website-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="license_number" data-testid="license-number-label">License Number</Label>
                    <Input
                      id="license_number"
                      value={barForm.license_number}
                      onChange={(e) => setBarForm({ ...barForm, license_number: e.target.value })}
                      placeholder="Optional"
                      data-testid="license-number-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-white/10">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="min-w-[140px]"
                    data-testid="save-bar-profile-btn"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Bar Profile'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
